import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { parseISO } from 'date-fns'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterContactpersoon } from '@components/toolbar/FilterContactpersoon'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { filterVerantwoordelijkeOptionsByOrganisatie } from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../core/components/SelectObject'
import { SelectString } from '../../../core/components/SelectString'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { queryKey } from '../hooks/useWerklijsten'
import type { FetchSubprocessenFilters } from '../types/FetchSubprocessen'

interface Props {
  filters: FetchSubprocessenFilters
  initialFilters: FetchSubprocessenFilters
  onChange: (filters: FetchSubprocessenFilters) => void
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    coordinerenden: string[]
    labels: FilterOption[]
    hoofdleiding: {
      gebieden: FilterOption[]
      netbeheerders: string[]
      verantwoordelijken: GroupedFilterOption[]
      typeWerkTypes: string[]
      gemeenten: string[]
    }
    processtappen: ProcesstapHoofdleiding[]
  }
}

export const SubprocesWerklijstenFilters = ({
  filters,
  initialFilters,
  onChange,
  onRefresh,
  onReset,
  filterOptions: {
    labels: labelOptions,
    hoofdleiding: { gebieden, verantwoordelijken, typeWerkTypes, gemeenten },
    processtappen,
  },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters({
      filters,
      initialFilters,
      onChange: (filters) => onChange(filters),
      onRefresh,
      onReset,
    })
  const { organisatie: userOrganisatie } = useAuthenticatedUser()

  const verantwoordelijkeOptions = filterVerantwoordelijkeOptionsByOrganisatie(
    verantwoordelijken,
    userOrganisatie,
  )

  const {
    gebied,
    myList,
    startUitvoeringWeekTot,
    startUitvoeringWeekVan,
    fromDate,
    toDate,
    verantwoordelijke,
    contactpersoon,
    processtatus,
    typeWerk,
    gemeente,
    labels,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          options={gebieden}
          label="Gebied"
          filterKey="gebied"
          value={gebied}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <SelectObject
            multiple
            options={labelOptions}
            label="Labels"
            value={labels}
            onChange={(value) => handleFilterChange({ labels: value })}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <SelectObject
          options={verantwoordelijkeOptions}
          label="Projectverantwoordelijke"
          value={verantwoordelijke}
          onChange={(value) => handleFilterChange({ verantwoordelijke: value })}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterContactpersoon
          value={contactpersoon}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={typeWerk}
          options={typeWerkTypes}
          label="Type werk"
          onChange={(value) => handleFilterChange({ typeWerk: value })}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Opleverdatum"
          fromDate={fromDate}
          toDate={toDate}
        />
      </FilterGridItem>

      <FilterGridItem>
        <TextField
          variant="standard"
          value={startUitvoeringWeekVan}
          label={'Project gepland van'}
          fullWidth
          onChange={(e) =>
            handleFilterChange({ startUitvoeringWeekVan: e.target.value })
          }
          error={
            !!startUitvoeringWeekVan &&
            startUitvoeringWeekVan !== '' &&
            isNaN(parseISO(startUitvoeringWeekVan).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>

      <FilterGridItem>
        <TextField
          variant="standard"
          value={startUitvoeringWeekTot}
          label={'Project gepland tot'}
          fullWidth
          onChange={(e) =>
            handleFilterChange({ startUitvoeringWeekTot: e.target.value })
          }
          error={
            !!startUitvoeringWeekTot &&
            startUitvoeringWeekTot !== '' &&
            isNaN(parseISO(startUitvoeringWeekTot).getTime())
          }
          placeholder="bijv. 2023-W01"
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={processtatus}
          options={processtappen}
          label="Processtap"
          onChange={(value) => handleFilterChange({ processtatus: value })}
        />
      </FilterGridItem>

      <FilterGridItem>
        <SelectString
          multiple
          value={gemeente}
          options={gemeenten}
          label="Gemeente"
          onChange={(value) => handleFilterChange({ gemeente: value })}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip title="Reset filters">
          <IconButton data-testid="clearbutton" onClick={handleReset}>
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoeken">
          <IconButton data-testid="filterbutton" onClick={handleZoeken}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
